import type { Logger } from "@expert/logging";

import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

import type { ExpertUniversitySimulatedVoiceTask } from "@/expert-university";
import type { TrainingTask } from "@/sdk";

import { Panels } from "@/panels";

import { useWorkspaceContentPanels } from "./hooks/useWorkspaceContentPanels";
import { WorkspaceContentLeftSidebarSlot } from "./WorkspaceContentLeftSidebarSlot";
import { WorkspaceContentLeftTopASlot } from "./WorkspaceContentLeftTopASlot";
import { WorkspaceContentRightSidebarSlot } from "./WorkspaceContentRightSidebarSlot";
import { WorkspaceContentTopbarSlot } from "./WorkspaceContentTopbarSlot";

interface VoiceWorkspaceContentLegacyProps {
    activeExpertUniversityTask: ExpertUniversitySimulatedVoiceTask | null;
    activeTrainingTask: TrainingTask | null;
    callControlsEnabled: boolean;
    customerPanelEnabled: boolean;
    customerSession: boolean;
    hasActiveVoiceTask: boolean;
    hasCallbackWaiting: boolean;
    isOutboundEnabled: boolean | undefined;
    isRealCustomerPanelEnabledForTraining: boolean | undefined;
    logger: Logger;
    sessionId: string;
    showExpertUniversity: undefined | boolean;
    showRealtimeTranscript: boolean;
    startedTimestamp: number;
}

/**
 * Legacy voice workspace layout (rendered when `isVoiceWorkspaceRedesignEnabled` is false). Owns its
 * own `useWorkspaceContentPanels` (and its store-writing hooks) so the two layouts never double-mount.
 */
export function VoiceWorkspaceContentLegacy({
    activeExpertUniversityTask,
    activeTrainingTask,
    callControlsEnabled,
    customerPanelEnabled,
    customerSession,
    hasActiveVoiceTask,
    hasCallbackWaiting,
    isOutboundEnabled,
    isRealCustomerPanelEnabledForTraining,
    logger,
    sessionId,
    showExpertUniversity,
    showRealtimeTranscript,
    startedTimestamp,
}: VoiceWorkspaceContentLegacyProps) {
    const theme = useMantineTheme();
    const isLargeScreen = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
    const { flowSlots, guideToolboxCommand, leftBottomConfig, leftTopConfig, rightBottomConfig, rightTopConfig } =
        useWorkspaceContentPanels({
            customerSession,
            isLargeScreen,
            logger,
            sessionId,
        });

    return (
        <Panels
            leftSidebarSlot={
                <WorkspaceContentLeftSidebarSlot
                    showRealtimeTranscript={showRealtimeTranscript}
                    taskStartTimeInMs={startedTimestamp}
                />
            }
            leftSidebarConfig={{
                isCollapsed: Boolean(showExpertUniversity),
            }}
            topbarSlot={<WorkspaceContentTopbarSlot />}
            topbarConfig={{
                isCollapsed:
                    Boolean(showExpertUniversity) ||
                    !callControlsEnabled ||
                    (!isOutboundEnabled && !hasActiveVoiceTask && !hasCallbackWaiting),
            }}
            rightSidebarSlot={
                <WorkspaceContentRightSidebarSlot
                    activeExpertUniversityTask={activeExpertUniversityTask}
                    customerSession={customerSession}
                    customerPanelEnabled={customerPanelEnabled}
                    isRealCustomerPanelEnabledForTraining={isRealCustomerPanelEnabledForTraining}
                />
            }
            rightSidebarConfig={{
                isCollapsed: Boolean(showExpertUniversity),
            }}
            leftTopASlot={
                <WorkspaceContentLeftTopASlot
                    showExpertUniversity={showExpertUniversity}
                    activeTrainingTask={activeTrainingTask}
                    guideToolboxCommand={guideToolboxCommand}
                />
            }
            leftTopBSlot={flowSlots.leftTopBSlot}
            leftTopConfig={leftTopConfig}
            rightBottomASlot={flowSlots.rightBottomASlot}
            rightBottomConfig={rightBottomConfig}
            rightTopASlot={flowSlots.rightTopASlot}
            rightTopConfig={rightTopConfig}
            leftBottomBSlot={flowSlots.leftBottomBSlot}
            leftBottomConfig={leftBottomConfig}
        />
    );
}
